import { useCallback } from 'react';

import { CONFIRM_POPUP_MODE, useConfirmPopup } from 'src/components/ConfirmPopup';
import { FEATURE_LIMITS } from 'src/data/featureLimits';
import { getLanguageWithoutFormality, isSameLanguage } from 'src/data/generation';
import { getToolChannel } from 'src/data/generationTool';
import { DRAFT_TOV_ID } from 'src/data/toneOfVoice';
import { PAGES, useCreateCustomModel, useErrorHandler } from 'src/services';
import {
  useBrandVoicesActions,
  useCustomerCanCreateFeatureItem,
  useNotificationActions,
  useProjectActions,
} from 'src/store';
import { useBrandVoicePopup } from 'src/views/BrandVoicePopup';
import { useToneOfVoicePopup } from 'src/views/ToneOfVoicePopup';
import { useUpgradePopup } from 'src/views/UpgradePopup';

import { formatBrandVoiceChildRoute, formatRoute } from '../../lib/routing';
import { ROUTE_PARAMS, ROUTES } from '../../routes';

import { useEvents } from './useEvents';

export const useHandlers = ({ onSelect, generationTool, onChange, language }) => {
  const notificationActions = useNotificationActions();
  const projectActions = useProjectActions();
  const brandVoicesActions = useBrandVoicesActions();
  const handleError = useErrorHandler();
  const openToneOfVoicePopup = useToneOfVoicePopup();
  const createCustomModel = useCreateCustomModel(generationTool);
  const channel = getToolChannel(generationTool);
  const openUpgradePopup = useUpgradePopup();
  const openConfirmPopup = useConfirmPopup();
  const openBrandVoicePopup = useBrandVoicePopup();
  const { trackClickedToneOfVoice, trackClickedCreateToneOfVoice } = useEvents(generationTool);

  const customerCanCreateToneOfVoice = useCustomerCanCreateFeatureItem(
    FEATURE_LIMITS.tonesOfVoicePerCustomer
  );

  const handleOpenBrandVoicePage = useCallback(() => {
    const brandVoices = brandVoicesActions.getBrandVoices();
    const defaultBrandVoice = brandVoicesActions.getDefaultBrandVoice();
    if (!defaultBrandVoice || brandVoices.length === 0) {
      return null;
    }
    const brandVoice = defaultBrandVoice || brandVoices[0];
    window.open(
      formatRoute(formatBrandVoiceChildRoute(brandVoice.id, ROUTES.TONE_OF_VOICE), {
        [ROUTE_PARAMS.createNew]: true,
      }),
      '_blank'
    );
  }, [brandVoicesActions]);

  const handleTovMenuOpen = useCallback(
    (event) => {
      event?.stopPropagation();
      projectActions.openToneOfVoiceMenu();
      trackClickedToneOfVoice();
    },
    [projectActions, trackClickedToneOfVoice]
  );

  const handleTovMenuClose = useCallback(
    (event) => {
      event?.stopPropagation();
      projectActions.closeToneOfVoiceMenu();
    },
    [projectActions]
  );

  const handleMenuSelect = useCallback(
    (tov) => {
      if (onSelect) {
        onSelect({
          selectedTov: tov,
        });
      } else {
        projectActions.updateToneOfVoice(tov);
      }
      handleTovMenuClose();
      onChange?.();
    },
    [handleTovMenuClose, projectActions, onSelect, onChange]
  );

  const handleAddToneOfVoice = useCallback(
    async ({
      shouldActivateManualStep,
      shouldActiveDraftMode,
      shouldActivateLastStep,
      shouldOnlyTrain,
      draftToneOfVoice,
    }) => {
      let newBrandVoice;
      const brandVoices = brandVoicesActions.getBrandVoices();
      const defaultBrandVoice = brandVoicesActions.getDefaultBrandVoice();
      if (!customerCanCreateToneOfVoice && (shouldActivateLastStep || shouldOnlyTrain)) {
        openUpgradePopup(FEATURE_LIMITS.tonesOfVoicePerCustomer);
        return;
      }

      const brandVoiceSameLanguage = isSameLanguage(defaultBrandVoice.language, language)
        ? defaultBrandVoice
        : brandVoices.find((brandVoice) => isSameLanguage(brandVoice.language, language));

      const brandVoiceNoLanguage = !defaultBrandVoice.language
        ? defaultBrandVoice
        : brandVoices.find((brandVoice) => !brandVoice.language);

      if (shouldActivateLastStep && !brandVoiceSameLanguage && !brandVoiceNoLanguage) {
        const confirmed = await openConfirmPopup({
          mode: CONFIRM_POPUP_MODE.confirmation,
          title: "Tone of voice language doesn't match brand voice",
          content:
            'To save this tone of voice, create a brand voice in the same language as the target audience.',
          buttons: {
            confirm: 'Create new brand voice',
            cancel: 'Close',
          },
        });
        if (!confirmed) {
          return;
        }
        newBrandVoice = await openBrandVoicePopup({
          noRedirect: true,
          language,
          lockLanguage: true,
        });

        if (!newBrandVoice) {
          handleTovMenuClose();
          return;
        }
      }

      trackClickedCreateToneOfVoice();

      try {
        const result = await openToneOfVoicePopup({
          brandVoice: newBrandVoice || brandVoiceSameLanguage || brandVoiceNoLanguage,
          onCreateCustomModel: channel ? createCustomModel : null,
          page: PAGES.editor,
          shouldActivateManualStep,
          shouldActivateLastStep,
          shouldOnlyTrain,
          shouldActiveDraftMode,
          toneOfVoice: draftToneOfVoice,
          language: getLanguageWithoutFormality(language),
        });
        if (result?.toneOfVoice) {
          notificationActions.displaySuccess(`Tone of voice '${result.toneOfVoice.name}' created`);
          projectActions.clearDraftToneOfVoice();
          projectActions.updateToneOfVoice(result.toneOfVoice.id);
          if (onSelect) {
            onSelect({
              selectedTov: result.toneOfVoice.id,
            });
          }
        } else if (result?.draftToneOfVoice) {
          if (onSelect) {
            onSelect({
              selectedTov: DRAFT_TOV_ID,
              selectedCustomModelId: null,
              draftToneOfVoice: { ...result.draftToneOfVoice, name: '' },
            });
          } else {
            projectActions.setDraftToneOfVoice(result.draftToneOfVoice);
          }
        }
        onChange?.();
      } catch (error) {
        handleError(error);
      } finally {
        handleTovMenuClose();
      }
    },
    [
      brandVoicesActions,
      customerCanCreateToneOfVoice,
      trackClickedCreateToneOfVoice,
      openUpgradePopup,
      language,
      openConfirmPopup,
      openBrandVoicePopup,
      openToneOfVoicePopup,
      channel,
      createCustomModel,
      onChange,
      notificationActions,
      projectActions,
      onSelect,
      handleError,
      handleTovMenuClose,
    ]
  );

  const handleSelectCustomModel = useCallback(
    (customModelId) => {
      handleTovMenuClose();
      if (onSelect) {
        onSelect({
          generationCustomModelId: customModelId,
        });
      } else {
        projectActions.setSelectedCustomModelId(customModelId);
      }
      onChange?.();
    },
    [handleTovMenuClose, projectActions, onSelect, onChange]
  );

  const handleSelectDraftToneOfVoice = useCallback(
    (draftToneOfVoice) => {
      if (onSelect) {
        onSelect({
          selectedTov: DRAFT_TOV_ID,
          selectedCustomModelId: null,
          draftToneOfVoice: { ...draftToneOfVoice, name: '' },
        });
      } else {
        projectActions.selectDraftToneOfVoice();
      }
    },
    [onSelect, projectActions]
  );

  return {
    handleTovMenuOpen,
    handleTovMenuClose,
    handleMenuSelect,
    handleAddToneOfVoice,
    handleSelectCustomModel,
    handleSelectDraftToneOfVoice,
    handleOpenBrandVoicePage,
  };
};
